import React from 'react';
import { Box, Grid, Paper, Typography, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  PlayArrow as GettingStartedIcon,
  Dashboard as BoardIcon,
  Payments as PayoutIcon,
  AccountBalanceWallet as AccountingIcon,
  Toll as CreditsIcon,
  SportsFootball as GameIcon,
  Palette as ThemesIcon,
  Notifications as NotificationsIcon,
  QuestionAnswer as FAQIcon,
  ArrowForward as ArrowIcon,
  MenuBook as GlossaryIcon,
  Group as GroupIcon,
  Security as SecurityIcon,
  LiveTv as LiveIcon,
} from '@mui/icons-material';
import { SupportLayout } from '../../components/support';
import { useDocumentTitle } from '../../hooks/core';

interface TopicCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  path: string;
  color: string;
}

const TopicCard: React.FC<TopicCardProps> = ({ title, description, icon, path, color }) => {
  const navigate = useNavigate();

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        height: '100%',
        border: 1,
        borderColor: 'divider',
        cursor: 'pointer',
        transition: 'all 0.2s',
        '&:hover': {
          borderColor: color,
          transform: 'translateY(-2px)',
          boxShadow: 2,
        },
      }}
      onClick={() => navigate(path)}
    >
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: 2,
          bgcolor: `${color}15`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 2,
          color: color,
        }}
      >
        {icon}
      </Box>
      <Typography variant="h6" gutterBottom fontWeight="bold">
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {description}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', color: color }}>
        <Typography variant="body2" fontWeight="600">
          Learn more
        </Typography>
        <ArrowIcon fontSize="small" sx={{ ml: 0.5 }} />
      </Box>
    </Paper>
  );
};

export const SupportHomePage: React.FC = () => {
  useDocumentTitle('Support Center');
  const navigate = useNavigate();

  const topics = [
    {
      title: 'Getting Started',
      description: 'New to SquareUp? Learn the basics of creating and managing your first sports squares board.',
      icon: <GettingStartedIcon fontSize="large" />,
      path: '/support/getting-started',
      color: '#4caf50',
    },
    {
      title: 'Board Management',
      description: 'Everything about creating boards, managing players, randomizing numbers, and board status.',
      icon: <BoardIcon fontSize="large" />,
      path: '/support/boards',
      color: '#2196f3',
    },
    {
      title: 'Game Mechanics',
      description: 'How winning squares are determined, score recording, and the math behind the game.',
      icon: <GameIcon fontSize="large" />,
      path: '/support/game-mechanics',
      color: '#9c27b0',
    },
    {
      title: 'Payout Options',
      description: 'Configure quarter payouts, reverse payouts, +N rules, neighbor & box neighbor bonuses, bad numbers, and custom categories.',
      icon: <PayoutIcon fontSize="large" />,
      path: '/support/payouts',
      color: '#ff9800',
    },
    {
      title: 'Accounting & Ledger',
      description: 'Track who owes what, record payments, manage winnings, and settle balances.',
      icon: <AccountingIcon fontSize="large" />,
      path: '/support/accounting',
      color: '#f44336',
    },
    {
      title: 'Credits & Payments',
      description: 'Purchase board credits, manage subscriptions, and understand the credit system.',
      icon: <CreditsIcon fontSize="large" />,
      path: '/support/credits',
      color: '#00bcd4',
    },
    {
      title: 'Themes',
      description: 'Customize your boards with themes. Browse the store, create custom designs, and import/export.',
      icon: <ThemesIcon fontSize="large" />,
      path: '/support/themes',
      color: '#e91e63',
    },
    {
      title: 'Notifications',
      description: 'Stay informed about wins, board updates, and system announcements with real-time notifications.',
      icon: <NotificationsIcon fontSize="large" />,
      path: '/support/notifications',
      color: '#ff5722',
    },
    {
      title: 'Participation Modes',
      description: 'Run pools with 25, 50, or 100 players using cell grouping modes for different group sizes.',
      icon: <GroupIcon fontSize="large" />,
      path: '/support/participation-modes',
      color: '#673ab7',
    },
    {
      title: 'Live Games',
      description: 'Connect boards to real sports games for live countdowns, scores, team logos, and automatic team branding.',
      icon: <LiveIcon fontSize="large" />,
      path: '/support/live-games',
      color: '#ef4444',
    },
    {
      title: 'Data Deletion & Privacy',
      description: 'Manage connected accounts, disconnect social logins, or request deletion of your account data.',
      icon: <SecurityIcon fontSize="large" />,
      path: '/support/data-deletion',
      color: '#607d8b',
    },
  ];

  return (
    <SupportLayout
      title="Welcome to the SquareUp Support Center"
      description="Find answers to your questions about running sports squares pools. Select a topic below or search for specific help."
    >
      {/* Quick start section */}
      <Paper
        elevation={0}
        sx={{
          p: 4,
          mb: 4,
          background: 'linear-gradient(135deg, #1976d2 0%, #7c3aed 100%)',
          color: 'white',
          borderRadius: 2,
        }}
      >
        <Grid container spacing={3} alignItems="center">
          <Grid size={{ xs: 12, md: 8 }}>
            <Typography variant="h5" gutterBottom fontWeight="bold">
              New to SquareUp?
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9, mb: 2 }}>
              Get up and running in minutes. Our getting started guide walks you through creating your first board, inviting players, and recording scores.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/support/getting-started')}
              sx={{
                bgcolor: 'white',
                color: 'primary.main',
                '&:hover': { bgcolor: 'grey.100' },
              }}
            >
              Start the Tutorial
            </Button>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }} sx={{ textAlign: 'center', display: { xs: 'none', md: 'block' } }}>
            <Box
              sx={{
                width: 120,
                height: 120,
                borderRadius: '50%',
                bgcolor: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
              }}
            >
              <GettingStartedIcon sx={{ fontSize: 60 }} />
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Topic cards */}
      <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ mb: 3 }}>
        Browse by Topic
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {topics.map((topic) => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={topic.path}>
            <TopicCard {...topic} />
          </Grid>
        ))}
      </Grid>

      {/* FAQ and Glossary section */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              bgcolor: 'grey.50',
              borderRadius: 2,
              border: 1,
              borderColor: 'divider',
              height: '100%',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <FAQIcon color="primary" fontSize="large" />
              <Typography variant="h6" fontWeight="bold">
                Frequently Asked Questions
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Answers to common questions about squares pools, payouts, and more.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => navigate('/support/faq')}
            >
              View FAQ
            </Button>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              bgcolor: 'grey.50',
              borderRadius: 2,
              border: 1,
              borderColor: 'divider',
              height: '100%',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <GlossaryIcon color="secondary" fontSize="large" />
              <Typography variant="h6" fontWeight="bold">
                Glossary of Terms
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Definitions of squares pool terminology like hits, payouts, and more.
            </Typography>
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => navigate('/support/glossary')}
            >
              View Glossary
            </Button>
          </Paper>
        </Grid>
      </Grid>

      {/* Quick links */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" gutterBottom fontWeight="bold">
          Popular Topics
        </Typography>
        <Grid container spacing={2}>
          {[
            { label: 'How do winning squares work?', path: '/support/game-mechanics#winning-squares' },
            { label: 'Setting up quarter payouts', path: '/support/payouts#quarter-payouts' },
            { label: 'What is the reverse payout?', path: '/support/payouts#reverse-payouts' },
            { label: 'Recording player payments', path: '/support/accounting#recording-payments' },
            { label: 'Managing offline players', path: '/support/boards#offline-players' },
            { label: 'How credits work', path: '/support/credits#how-credits-work' },
          ].map((link) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={link.path}>
              <Button
                variant="text"
                fullWidth
                onClick={() => navigate(link.path)}
                sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
              >
                {link.label}
              </Button>
            </Grid>
          ))}
        </Grid>
      </Box>
    </SupportLayout>
  );
};

export default SupportHomePage;
